class BundleOptionsAdvanced extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this.onVariantChange);
    this.allBundleOptions = this.querySelectorAll(".bundle-option");
    this.bundleOptionGroups = this.querySelectorAll(".bundle-option-group");
    this.bundleOptionGroupsDependent = this.querySelectorAll(".bundle-option-group-dependent");
    this.bundleVariants = [];
    this.productForm = document.getElementById(
      `ProductForm--${this.dataset.section}-${this.dataset.productId}`
    );
    this.addToCartButton = document.getElementById(`AddToCart--${this.dataset.section}`);
    this.selectedColor = new URLSearchParams(window.location.search).get("color");
    this.bundleDiscount = this.dataset.bundleDiscount || 0;
  }

  async connectedCallback() {
    await this.fetchBundleVariants();
    this.init();

    this.selectVariants();
    this.updatePrice();

    this.validateSelections();
  }

  async fetchBundleVariants() {
    try {
      const productHandle = this.dataset.productHandle;
      if (!productHandle) {
        console.error("Product handle not found");
        return;
      }

      const response = await fetch(`/products/${productHandle}?view=bundle-json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      this.bundleVariants = data.bundle_variants || [];
      console.log("BUNDLE VARIANTS loaded:", this.bundleVariants.length);
    } catch (error) {
      console.error("Error fetching bundle variants:", error);
      this.bundleVariants = [];
    }
  }

  init() {
    this.addToCartButton = this.getAddToCartButton();
    this.productForm = this.getProductForm();
    this.addToCartButton.addEventListener("click", (e) => this.addBundleToCart(e));
    this.setupQuantityListener();
  }

  getAddToCartButton() {
    if (this.addToCartButton) return this.addToCartButton;

    let parent = this.parentElement;
    while (parent) {
      const button = parent.querySelector("button[data-add-to-cart]");
      if (button) {
        return button;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  getProductForm() {
    if (this.productForm) return this.productForm;

    let parent = this.parentElement;
    while (parent) {
      const productForm = parent.querySelector("form[data-product-form]");
      if (productForm) {
        return productForm;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  onVariantChange() {
    this.selectVariants();
    this.updatePrice();
    this.validateSelections();
  }

  setupQuantityListener() {
    // Find the quantity counter element
    const quantityCounter = this.productForm?.querySelector("quantity-counter");
    if (quantityCounter) {
      // Listen for changes in the quantity input
      const quantityInput = quantityCounter.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.addEventListener("change", () => {
          this.onVariantChange();
        });
      }

      // Also listen for changes in the popout select if it exists
      const popoutSelect = quantityCounter.querySelector("popout-select");
      if (popoutSelect) {
        popoutSelect.addEventListener("change", () => {
          this.onVariantChange();
        });
      }

      // Listen for click events on quantity buttons
      const quantityButtons = quantityCounter.querySelectorAll(
        'button[name="increase"], button[name="decrease"]'
      );
      quantityButtons.forEach((button) => {
        button.addEventListener("click", () => {
          // Use setTimeout to ensure the quantity value has been updated
          setTimeout(() => {
            this.onVariantChange();
          }, 0);
        });
      });

      // Listen for popout option clicks
      const popoutOptions = quantityCounter.querySelectorAll("[data-popout-option]");
      popoutOptions.forEach((option) => {
        option.addEventListener("click", () => {
          // Use setTimeout to ensure the quantity value has been updated
          setTimeout(() => {
            this.onVariantChange();
          }, 0);
        });
      });
    }
  }

  addBundleToCart(e) {
    e.preventDefault();
    console.log("Adding bundle to cart");

    const mainProductJson = JSON.parse(
      document.querySelector("[data-product-json]")?.textContent || "{}"
    );
    const mainProductVariantId = mainProductJson.variants[0].id;
    
    const quantityInput = this.productForm?.querySelector('input[name="quantity"]');
    const currentQuantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    // Build componentItems including automatic_extra_products and their quantities
    const componentItems = [];

    this.selectedVariants.forEach((variant) => {
      // Add the main variant
      if (!variant.skip_as_line_item) {
        componentItems.push({
          id: variant.id.toString(),
          qty: variant.quantity || 1,
          price: (variant.price / 100).toString(),
        });
      }

      // Handle automatic_extra_products if present (now always an array of variant-like objects)
      if (
        Array.isArray(variant.automatic_extra_products) &&
        variant.automatic_extra_products.length > 0
      ) {
        let extraQuantities = [];
        if (Array.isArray(variant.automatic_extra_products_quantity)) {
          if (variant.automatic_extra_products_quantity.length === 1) {
            // If only one value, use it for all products
            extraQuantities = Array(variant.automatic_extra_products.length).fill(
              typeof variant.automatic_extra_products_quantity[0] === "number"
                ? variant.automatic_extra_products_quantity[0]
                : 1
            );
          } else {
            // Use the array as is, fallback to 1 if missing
            extraQuantities = variant.automatic_extra_products.map((_, idx) =>
              typeof variant.automatic_extra_products_quantity[idx] === "number"
                ? variant.automatic_extra_products_quantity[idx]
                : 1
            );
          }
        } else {
          // If not an array, fallback to 1 for all
          extraQuantities = Array(variant.automatic_extra_products.length).fill(1);
        }

        variant.automatic_extra_products.forEach((extraProduct, idx) => {
          // Only add if the product has an id
          if (extraProduct && extraProduct.id) {
            componentItems.push({
              id: extraProduct.id.toString(),
              qty: (extraQuantities[idx] || 1) * (variant.quantity || 1),
              price: (extraProduct.price / 100).toString(),
            });
          }
        });
      }
    });

    let formData = {
      items: [
        {
          id: mainProductVariantId,
          quantity: currentQuantity,
          properties: {
            Config: this.allSelectedOptions.join(", "),
            _components: JSON.stringify(componentItems),
            _bundleDiscount: this.bundleDiscount,
          },
        },
      ],
    };

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }

  handelize(str) {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace("*", "x")
      .replace(/'/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  matchesVariantDependency(variant) {
    const groups = [];
    if (Array.isArray(variant.match_option_values_1) && variant.match_option_values_1.length > 0) {
      groups.push(variant.match_option_values_1);
    }
    if (Array.isArray(variant.match_option_values_2) && variant.match_option_values_2.length > 0) {
      groups.push(variant.match_option_values_2);
    }

    if (groups.length === 0) return false;
    if (!Array.isArray(this.selectedOptions) || this.selectedOptions.length === 0) return false;

    // For each defined group, require at least one value to be present in this.selectedOptions
    return groups.every((group) =>
      group.some((value) =>
        this.selectedOptions.some((sel) => this.handelize(sel) === this.handelize(value))
      )
    );
  }

  selectVariants() {
    this.selectedVariants = [];
    this.allSelectedOptions = [];
    this.selectedOptions = [];

    for (let i = 0; i < this.bundleOptionGroups.length; i++) {
      const group = this.bundleOptionGroups[i];
      const groupNumber = group.dataset.group;
      const selectedColorContainer = group.querySelector(".selected-color");
      const selectedOptions = Array.from(group.querySelectorAll(`input:checked`)).map(
        (option) => option.value
      );
      const variant = this.bundleVariants.find(
        (variant) =>
          variant.group == groupNumber &&
          variant.options.every((option) =>
            selectedOptions.some(
              (selected) =>
                selected === option || this.handelize(selected) === this.handelize(option)
            )
          )
      );
      if (variant) {
        this.selectedVariants.push(variant);
        this.selectedOptions.push(...selectedOptions);
        this.allSelectedOptions.push(...selectedOptions);
      }
      if (selectedColorContainer) {
        selectedColorContainer.textContent = selectedOptions[0];
      } else {
        selectedColorContainer.textContent = "";
      }
    }

    if (this.selectedVariants.length > 0) {
      this.filterDependentOptions();
    }

    for (let i = 0; i < this.bundleOptionGroupsDependent.length; i++) {
      const group = this.bundleOptionGroupsDependent[i];
      const groupNumber = group.dataset.group;
      const selectedOption = group.querySelector(`input:checked`)?.value;

      const selectedVariant = this.bundleVariants.find(
        (variant) =>
          variant.group == groupNumber &&
          this.handelize(variant.option_name) == this.handelize(selectedOption) &&
          this.matchesVariantDependency(variant)
      );
      if (selectedVariant) {
        this.selectedVariants.push(selectedVariant);
        this.allSelectedOptions.push(selectedOption);
      }
    }

    // Update product image (similar to selectVariant method)
    if (this.selectedVariants.length > 0) {
      const firstVariant = this.selectedVariants[0];
      const images = firstVariant.images;

      // Find the image whose alt text (after "options:") contains the most options matching selectedOptions,
      // but only if all of its options are present in selectedOptions (no extra options allowed).
      let image = null;
      if (Array.isArray(images) && images.length > 0 && Array.isArray(this.allSelectedOptions)) {
        let maxMatches = -1;
        for (const img of images) {
          if (!img.alt) continue;
          const optionsMatch = img.alt.match(/options:([^\s]+)/);
          if (!optionsMatch) continue;
          const optionParts = optionsMatch[1].split(",");
          // Only consider this image if every optionPart is in selectedOptions (case-insensitive, handleized)
          const allInSelected = optionParts.every((part) =>
            this.allSelectedOptions.some((opt) => this.handelize(opt) === this.handelize(part))
          );
          if (!allInSelected) continue;
          // Count how many optionParts match (should be optionParts.length, but keep for maxMatches logic)
          const matches = optionParts.length;
          if (matches > maxMatches) {
            maxMatches = matches;
            image = img.src || img.url || null;
          }
        }
        // fallback: if no image found, use first image
        if (!image && images[0]) {
          image = images[0].src || images[0].url || null;
        }
      }

      if (image) {
        const widths = [
          180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 1950, 2100, 2260, 2450, 2700, 3000, 3350,
          3750, 4100, 4450, 4800, 5150, 5500, 5760,
        ];
        const srcset = widths.map((width) => `${image}&width=${width} ${width}w`).join(", ");
        const imgEl = document.querySelectorAll(
          ".product__page .product__slides .product__media-wrapper .image-wrapper img"
        )?.[1];
        if (imgEl) {
          imgEl.src = image;
          imgEl.srcset = srcset;

          const productPhotoParent = imgEl.closest(".product__photo");
          if (productPhotoParent) {
            productPhotoParent.setAttribute("data-image-src", image);
          }
        }
      }
    }

    console.log("SELECTED VARIANTS: ", this.selectedVariants);

    // Calculate total price including automatic_extra_products
    const originalPrice = this.selectedVariants.reduce((acc, variant) => {
      let total = 0;
      const qty = typeof variant.quantity === "number" ? variant.quantity : 1;

      if(!variant.skip_as_line_item) {
        total = acc + variant.price * qty;
      }

      // Handle automatic_extra_products
      if (
        Array.isArray(variant.automatic_extra_products) &&
        variant.automatic_extra_products.length > 0
      ) {
        // Get the quantities for the extra products
        let extraQuantities = [];
        if (Array.isArray(variant.automatic_extra_products_quantity)) {
          if (variant.automatic_extra_products_quantity.length === 1) {
            // If only one value, use it for all products
            extraQuantities = Array(variant.automatic_extra_products.length).fill(
              typeof variant.automatic_extra_products_quantity[0] === "number"
                ? variant.automatic_extra_products_quantity[0]
                : 1
            );
          } else {
            // Use the array as is, fallback to 1 if missing
            extraQuantities = variant.automatic_extra_products.map((_, idx) =>
              typeof variant.automatic_extra_products_quantity[idx] === "number"
                ? variant.automatic_extra_products_quantity[idx]
                : 1
            );
          }
        } else {
          // If not an array, fallback to 1 for all
          extraQuantities = Array(variant.automatic_extra_products.length).fill(1);
        }

        // Sum up the price for each extra product
        variant.automatic_extra_products.forEach((extraProduct, idx) => {
          // Try to get the price from the product or its first variant
          let extraPrice = 0;
          if (typeof extraProduct.price === "number") {
            extraPrice = extraProduct.price;
          } else if (
            Array.isArray(extraProduct.variants) &&
            extraProduct.variants.length > 0 &&
            typeof extraProduct.variants[0].price === "number"
          ) {
            extraPrice = extraProduct.variants[0].price;
          }
          // Multiply by the quantity for this extra product and the main variant quantity
          total += extraPrice * extraQuantities[idx] * qty;
        });
      }

      return total;
    }, 0);

    // Apply bundle discount if present
    if (this.bundleDiscount && parseFloat(this.bundleDiscount) > 0) {
      this.comparePrice = originalPrice;
      this.price = originalPrice * (1 - parseFloat(this.bundleDiscount) / 100);
    } else {
      this.price = originalPrice;
      this.comparePrice = null;
    }
  }

  filterDependentOptions() {
    // Loop through all dependent groups
    for (let i = 0; i < this.bundleOptionGroupsDependent.length; i++) {
      const group = this.bundleOptionGroupsDependent[i];
      const groupNumber = group.dataset.group;

      // Get all option inputs in this group
      const optionInputs = group.querySelectorAll('input[type="radio"]');

      // Get all variants for this group
      const groupVariants = this.bundleVariants.filter((variant) => variant.group == groupNumber);

      // Track which options are available and which are currently checked
      const availableOptions = [];
      let currentlyCheckedInput = null;

      // Show/hide each option based on whether it matches firstSelectedOptions
      optionInputs.forEach((input) => {
        // Skip optional custom option values
        if (input.id.includes("-custom_option_value-optional")) {
          return;
        }

        const optionName = input.value;
        const matchingVariant = groupVariants.find(
          (variant) =>
            this.handelize(variant.option_name) === this.handelize(optionName) &&
            this.matchesVariantDependency(variant)
        );

        // Show/hide the input and its label
        const label = group.querySelector(`label[for="${input.id}"]`);
        if (matchingVariant) {
          input.style.display = "block";
          if (label) {
            label.style.display = "block";
            label.style.visibility = "visible";
          }
          availableOptions.push(input);
        } else {
          input.style.display = "none";
          if (label) {
            label.style.display = "none";
            label.style.visibility = "hidden";
          }
          // If this hidden input was checked, we need to uncheck it
          if (input.checked) {
            input.checked = false;
            currentlyCheckedInput = input;
          }
        }
      });

      // If the currently checked option was hidden, select the first available option
      if (currentlyCheckedInput && availableOptions.length > 0) {
        const firstAvailableOption = availableOptions[0];
        firstAvailableOption.checked = true;
      }
    }
  }

  updatePrice() {
    const getPriceFormatted = (price) => { 
      // Use Shopify's money format from theme settings
      const moneyFormat =
        window.theme && window.theme.moneyFormat
          ? window.theme.moneyFormat
          : Shopify && Shopify.money_format
            ? Shopify.money_format
            : "{{ shop.money_format }}";
      
      let formattedPrice;
      if (window.theme.formatMoney) {
        formattedPrice = window.theme.formatMoney(price, moneyFormat);
      } else {
        // Fallback: Danish style
        formattedPrice = (price / 100).toLocaleString("da-DK", {
          style: "currency",
          currency: window.theme && window.theme.currency ? window.theme.currency : "DKK",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return formattedPrice;
    }

    // Find the price container and update the price display
    const priceWrapper = document.querySelector(".product__price__wrap [data-price-wrapper]");
    const priceWrap = document.querySelector(".product__price__wrap [data-product-price]");
    let comparePriceWrap = document.querySelector(".product__price__wrap [data-compare-price]");
    let compareTextWrap = document.querySelector(".product__price__wrap [data-compare-text]");
    const productSaleBadge = document.querySelector(".product__price__wrap [data-price-off-badge]");
    const priceOffAmount = document.querySelector(".product__price__wrap [data-price-off-amount]");
    const priceOffWrap = document.querySelector(".product__price__wrap [data-price-off]");

    if (priceWrap) {
      let formattedPrice = getPriceFormatted(this.price);
      priceWrap.textContent = formattedPrice;
      
      // Add sale class if there's a compare price
      if (this.comparePrice && this.comparePrice > this.price) {
        priceWrap.classList.add("product__price--sale");
      } else {
        priceWrap.classList.remove("product__price--sale");
      }
    }

    // Create compare price elements if they don't exist
    if (!compareTextWrap && priceWrapper) {
      compareTextWrap = document.createElement("span");
      compareTextWrap.className = "visually-hidden";
      compareTextWrap.setAttribute("data-compare-text", "");
      compareTextWrap.textContent = window.theme.strings.regularPrice || "Normal pris";
      priceWrapper.appendChild(compareTextWrap);
    }

    if (!comparePriceWrap && priceWrapper) {
      comparePriceWrap = document.createElement("s");
      comparePriceWrap.className = "product__price--strike";
      comparePriceWrap.setAttribute("data-compare-price", "");
      comparePriceWrap.style.display = "none";
      priceWrapper.appendChild(comparePriceWrap);
    }

    if (comparePriceWrap) {
      if (this.comparePrice && this.comparePrice > this.price) {
        comparePriceWrap.style.display = "inline";
        compareTextWrap.style.display = "inline";
        let formattedComparePrice = getPriceFormatted(this.comparePrice);
        comparePriceWrap.textContent = formattedComparePrice;

        // Calculate and display discount percentage
        const discountPercent = Math.round(((this.comparePrice - this.price) / this.comparePrice) * 100);
        
        // Show the price off wrapper
        if(priceOffWrap) {
          priceOffWrap.classList.remove("hidden");
        }
        
        if(productSaleBadge) {
          productSaleBadge.style.display = "inline";
          productSaleBadge.classList.remove("hidden");
        }
        
        if(priceOffAmount) {
          priceOffAmount.textContent = discountPercent + "%";
        }
      } else {
        comparePriceWrap.style.display = "none";
        if(compareTextWrap) {
          compareTextWrap.style.display = "none";
        }

        if(priceOffWrap) {
          priceOffWrap.classList.add("hidden");
        }

        if(productSaleBadge) {
          productSaleBadge.style.display = "none";
          productSaleBadge.classList.add("hidden");
        }
      }
    }
  }

  validateSelections() {
    //if not all bundle options are selected, disable the add to cart button
    if (
      Array.from(this.allBundleOptions).some((option) => !option.querySelector("input:checked"))
    ) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = window.theme.strings.bundleSelectAll;
      return;
    } else {
      this.addToCartButton.disabled = false;
      this.addToCartButton.textContent = window.theme.strings.addToCart;
    }

    //if any selected variant has an inventory_quantity of 0, disable the add to cart button
    if (this.selectedVariants.some((variant) => !variant.available)) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = window.theme.strings.soldOut;
      return;
    } else {
      this.addToCartButton.disabled = false;
      this.addToCartButton.textContent = window.theme.strings.addToCart;
    }
  }
}

if (!customElements.get("bundle-options-advanced")) {
  customElements.define("bundle-options-advanced", BundleOptionsAdvanced);
}
